/**
 * Подсказки адресов через Яндекс.Карты JS API 2.1.
 *
 * По умолчанию используем `ymaps.suggest()` — даёт полноценный prefix-search
 * с домами на улице (как в мобильном клиенте через HTTP
 * `suggest-maps.yandex.ru/v1/suggest`). Для этого `useYandexMaps` подгружает
 * JS API с параметром `&suggest_apikey=...` (по умолчанию равен основному
 * ключу JS API — в Яндекс.кабинете один ключ обычно закрывает оба сервиса:
 * «JS API и HTTP-Геокодер» + «Geosuggest API»).
 *
 * Если на ключе Geosuggest не подключён, Яндекс на первый же вызов
 * `ymaps.suggest()` бросает `FeatureRemovedError: Suggest is not available`.
 * В таком случае хук **автоматически переключается на fallback** —
 * `ymaps.geocode()`. Геокодер жив на основном ключе JS API, возвращает
 * структурированные компоненты адреса (`Address.Components`), но не отдаёт
 * список домов на улице по префиксу — дома появятся только если в строке
 * есть номер. Это терпимая деградация UX без полного выпадения функции.
 *
 * Контракт `AddressSuggestion` единый для обоих путей: `line1` (улица + дом
 * либо locality), `line2` (город, регион), `locality` (распознанный город).
 * UI (`AddressInput`, `OrderDrawer`) не зависит от выбранного источника.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useYandexMaps } from './useYandexMaps'

export interface AddressSuggestion {
  /** Полный адрес — используется как ключ и в callback'е caller'а. */
  value: string
  /** Полная человекочитаемая строка (для tooltip/резервного отображения). */
  displayName: string
  /** Первая строка карточки — «улица + дом» (или locality, если адрес «общий»). */
  line1: string
  /** Вторая строка — «город, регион». Может быть `null`. */
  line2: string | null
  /** Распознанный город (`kind=locality` геокодера, либо извлечённый из displayName). */
  locality: string | null
}

interface SuggestOptions {
  /**
   * Список «известных» городов (например, активные строки `rates_k1`).
   * Используется suggest-путём для парсинга `displayName` (если в ответе
   * suggest встретилось одно из этих имён — оно становится `locality`).
   * Geocode-путь для locality использует `kind=locality` напрямую.
   */
  knownCities?: string[]
}

const DEBOUNCE_MS = 280
const MIN_CHARS = 3
const RESULTS_LIMIT = 10

/**
 * Модульный флаг — переключается в `false` навсегда после первой ошибки
 * `FeatureRemovedError` от `ymaps.suggest()`. Это значит, что текущий ключ
 * Geosuggest не поддерживает, и мы дальше живём на `ymaps.geocode()`.
 */
let suggestAvailable = true

/* ───────────────── Geocode-путь (fallback) ───────────────── */

interface AddressComponent {
  kind: string | string[]
  name: string
}

function kindList(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map((k) => String(k).toLowerCase())
  if (typeof raw === 'string') return [raw.toLowerCase()]
  return []
}

interface ParsedAddress {
  line1: string
  line2: string | null
  locality: string | null
  text: string
}

function parseGeocoderMeta(meta: unknown): ParsedAddress | null {
  if (!meta || typeof meta !== 'object') return null
  const m = meta as {
    text?: string
    Address?: {
      country_code?: string
      formatted?: string
      Components?: AddressComponent[]
    }
  }
  const address = m.Address
  if (!address) return null
  if (address.country_code && address.country_code !== 'RU') return null

  const components = Array.isArray(address.Components) ? address.Components : []
  let street: string | null = null
  let house: string | null = null
  let locality: string | null = null
  let province: string | null = null
  let area: string | null = null
  for (const c of components) {
    const name = (c?.name ?? '').toString().trim()
    if (!name) continue
    for (const k of kindList(c.kind)) {
      if (k === 'street') street = name
      else if (k === 'house') house = name
      else if (k === 'locality') locality = name
      else if (k === 'province') province = name
      else if (k === 'area') area = name
    }
  }

  const fullText = (m.text || address.formatted || '').trim()
  const streetHouse = [street, house].filter(Boolean).join(' ').trim()
  const line1 = streetHouse || locality || fullText
  if (!line1) return null

  const line2Parts: string[] = []
  if (locality && locality !== line1) line2Parts.push(locality)
  if (province && province.toLowerCase() !== (locality ?? '').toLowerCase()) {
    line2Parts.push(province)
  } else if (
    area &&
    area.toLowerCase() !== (locality ?? '').toLowerCase() &&
    !province
  ) {
    line2Parts.push(area)
  }
  const line2 = line2Parts.length > 0 ? line2Parts.join(', ') : null

  return { line1, line2, locality, text: fullText || line1 }
}

/* ───────────────── Suggest-путь (preferred) ───────────────── */

/**
 * Раскладка `displayName` ответа `ymaps.suggest()` на line1/line2/locality.
 *
 * `displayName` приходит в виде «Оболенский переулок, 1, Москва, Россия» — без
 * структурированных компонент. Эвристика:
 *
 * 1. Дробим по запятым.
 * 2. Убираем заведомо лишнее «Россия» в конце.
 * 3. Если среди частей встретилось `knownCities` — это и есть `locality`,
 *    line1 — всё до него, line2 — locality + всё после.
 * 4. Иначе считаем, что последняя часть — регион/локалити, а первые 1–2 —
 *    улица/дом.
 */
function parseSuggestDisplayName(
  raw: string,
  knownCities: string[],
): ParsedAddress {
  const parts = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((p) => !/^росси[яи]$/i.test(p))

  if (parts.length === 0) {
    return { line1: raw, line2: null, locality: null, text: raw }
  }

  const lowerCities = knownCities.map((c) => c.toLowerCase())
  let cityIdx = -1
  for (let i = 0; i < parts.length; i++) {
    if (lowerCities.includes(parts[i].toLowerCase())) {
      cityIdx = i
      break
    }
  }

  if (cityIdx > 0) {
    const line1 = parts.slice(0, cityIdx).join(', ')
    const tailParts = parts.slice(cityIdx)
    const line2 = tailParts.join(', ')
    return {
      line1,
      line2,
      locality: parts[cityIdx],
      text: raw,
    }
  }

  // Эвристика без knownCities: последняя часть — регион/город.
  if (parts.length === 1) {
    return { line1: parts[0], line2: null, locality: parts[0], text: raw }
  }
  const line1 = parts.slice(0, parts.length - 1).join(', ')
  const last = parts[parts.length - 1]
  return {
    line1,
    line2: last,
    locality: last,
    text: raw,
  }
}

/* ───────────────── Хук ───────────────── */

export function useAddressSuggest(options: SuggestOptions = {}) {
  const { ready, isConfigured } = useYandexMaps()
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reqIdRef = useRef(0)

  const knownCities = options.knownCities ?? []
  const knownCitiesKey = useMemo(() => knownCities.join('|'), [knownCities])

  const suggest = useCallback(
    (text: string) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      const trimmed = text.trim()
      if (!ready || !isConfigured || trimmed.length < MIN_CHARS) {
        setSuggestions([])
        return
      }
      const reqId = ++reqIdRef.current
      timerRef.current = setTimeout(async () => {
        setLoading(true)
        try {
          /* eslint-disable @typescript-eslint/no-explicit-any */
          const ymaps = (window as any).ymaps
          /* eslint-enable @typescript-eslint/no-explicit-any */

          const out: AddressSuggestion[] = []
          const seen = new Set<string>()

          // Сначала пытаемся suggest — он быстрее и отдаёт дома на улице.
          // Если упал FeatureRemovedError — навсегда выключаем suggest и
          // тут же переходим на geocode-ветку (без второго запроса к API
          // здесь — пользователь увидит результаты со следующим вводом).
          if (suggestAvailable && ymaps?.suggest) {
            try {
              const items: Array<{ value?: string; displayName?: string }> =
                await ymaps.suggest(trimmed, { results: RESULTS_LIMIT })
              const cities = knownCitiesKey ? knownCitiesKey.split('|') : []
              for (const it of items ?? []) {
                const display = (it.displayName ?? it.value ?? '').toString()
                const value = (it.value ?? it.displayName ?? '').toString()
                if (!display) continue
                const parsed = parseSuggestDisplayName(display, cities)
                const key = `${parsed.line1}|${parsed.line2 ?? ''}`.toLowerCase()
                if (seen.has(key)) continue
                seen.add(key)
                out.push({
                  value,
                  displayName: display,
                  line1: parsed.line1,
                  line2: parsed.line2,
                  locality: parsed.locality,
                })
              }
            } catch (err) {
              // FeatureRemovedError — Geosuggest не подключён к ключу.
              // Навсегда выключаем suggest и проваливаемся в ветку geocode.
              const name = (err as { name?: string } | undefined)?.name
              if (name === 'FeatureRemovedError') {
                suggestAvailable = false
                console.warn(
                  '[useAddressSuggest] ymaps.suggest недоступен (Geosuggest не подключён к ключу), переключаюсь на ymaps.geocode',
                )
              } else {
                throw err
              }
            }
          }

          if (out.length === 0 && ymaps?.geocode) {
            // Fallback: геокодер. Возвращает «лучшее совпадение», без списка
            // домов на улице по префиксу — это ограничение API.
            const result = await ymaps.geocode(trimmed, {
              results: RESULTS_LIMIT,
              boundedBy: [
                [41, 19],
                [82, 191],
              ],
              strictBounds: false,
            })
            const collection = result?.geoObjects
            if (collection?.each) {
              /* eslint-disable @typescript-eslint/no-explicit-any */
              collection.each((geoObject: any) => {
                try {
                  const meta = geoObject?.properties?.get?.(
                    'metaDataProperty.GeocoderMetaData',
                  )
                  const parsed = parseGeocoderMeta(meta)
                  if (!parsed) return
                  const key = `${parsed.line1}|${parsed.line2 ?? ''}`.toLowerCase()
                  if (seen.has(key)) return
                  seen.add(key)
                  out.push({
                    value: parsed.text,
                    displayName: parsed.text,
                    line1: parsed.line1,
                    line2: parsed.line2,
                    locality: parsed.locality,
                  })
                } catch (e) {
                  console.warn('[useAddressSuggest] geoObject parse failed', e)
                }
              })
              /* eslint-enable @typescript-eslint/no-explicit-any */
            }
          }

          if (out.length === 0 && !ymaps?.suggest && !ymaps?.geocode) {
            console.warn(
              '[useAddressSuggest] ymaps API not available (neither suggest nor geocode)',
            )
          }

          if (reqIdRef.current === reqId) setSuggestions(out)
        } catch (err) {
          console.warn('[useAddressSuggest] failed', err)
          if (reqIdRef.current === reqId) setSuggestions([])
        } finally {
          if (reqIdRef.current === reqId) setLoading(false)
        }
      }, DEBOUNCE_MS)
    },
    [ready, isConfigured, knownCitiesKey],
  )

  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setSuggestions([])
  }, [])

  /**
   * Прямой геокод одной строки адреса для определения города (`locality`).
   * Используется в форме «Новый заказ», когда пользователь ввёл адрес вручную
   * и не выбрал подсказку: чтобы посчитать preview-стоимость и проверить
   * активность города в `rates_k1`, фронту нужен распознанный город.
   *
   * Всегда идёт через `ymaps.geocode` (структурированные компоненты), даже если
   * suggest-режим включён — нам нужен надёжный `locality`, а не результат
   * парсинга строки.
   */
  const resolveLocality = useCallback(
    async (text: string): Promise<string | null> => {
      const trimmed = text.trim()
      if (!ready || !isConfigured || trimmed.length < MIN_CHARS) return null
      try {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const ymaps = (window as any).ymaps
        if (!ymaps?.geocode) return null
        const result = await ymaps.geocode(trimmed, {
          results: 1,
          boundedBy: [
            [41, 19],
            [82, 191],
          ],
          strictBounds: false,
        })
        const collection = result?.geoObjects
        const first = collection?.get?.(0)
        const meta = first?.properties?.get?.(
          'metaDataProperty.GeocoderMetaData',
        )
        const parsed = parseGeocoderMeta(meta)
        return parsed?.locality ?? null
        /* eslint-enable @typescript-eslint/no-explicit-any */
      } catch (err) {
        console.warn('[useAddressSuggest] resolveLocality failed', err)
        return null
      }
    },
    [ready, isConfigured],
  )

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    [],
  )

  return { suggest, suggestions, loading, clear, isConfigured, resolveLocality }
}
